import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Smartphone } from 'lucide-react';
import { useInstagramConnection } from 'contexts/InstagramConnectionContext';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';

interface DeviceConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeviceConfirmationModal: React.FC<DeviceConfirmationModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { connectionState } = useInstagramConnection();
  const { isDark } = useTheme();

  // Auto-close when connection step changes away from 'confirmation'
  useEffect(() => {
    if (isOpen && connectionState.step !== 'confirmation') {
      onClose();
    }
  }, [isOpen, connectionState.step, onClose]);

  if (!isOpen) return null;

  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const overlayBg = isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#62748E';
  const closeButtonColor = isDark ? '#90A1B9' : '#314158';
  const iconBg = isDark ? '#1E293B' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const waitingBg = isDark ? 'rgba(43, 127, 255, 0.1)' : '#F0F7FF';
  const waitingBorder = isDark ? '#1E40AF' : '#BFDBFE';
  const waitingText = isDark ? '#93C5FD' : '#1D4ED8';

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: overlayBg, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-[440px] mx-4 flex flex-col"
        style={{
          background: modalBg,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between"
          style={{
            padding: '24px',
            borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: iconBg }}
            >
              <Smartphone size={20} color={iconColor} />
            </div>
            <h2
              className="text-xl font-bold leading-7"
              style={{ color: titleColor }}
            >
              {t('addAccount.confirmDeviceLogin') || 'Please confirm device login'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center transition-opacity hover:opacity-70 flex-shrink-0"
            style={{
              width: '28px',
              height: '28px',
              background: 'rgba(255, 255, 255, 0.01)',
              borderRadius: '6px',
              padding: '6px',
              color: closeButtonColor,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div
          className="flex flex-col"
          style={{ padding: '24px', gap: '20px', background: modalBg }}
        >
          {/* Description from WebSocket message */}
          <p
            className="font-normal leading-6"
            style={{ color: descriptionColor, fontSize: '15px' }}
          >
            {connectionState.status || t('addAccount.confirmDeviceLoginDescription') || 'Please confirm the Instagram login on your device.'}
          </p>

          {/* Waiting indicator */}
          <div
            className="flex items-center gap-3 p-3 rounded-lg"
            style={{
              background: waitingBg,
              border: `1px solid ${waitingBorder}`,
            }}
          >
            <Spinner size="small" color="primary" />
            <span
              className="text-sm font-medium"
              style={{ color: waitingText }}
            >
              {t('addAccount.waitingForConfirmation') || 'Waiting for confirmation...'}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeviceConfirmationModal;
